import {ChargeEvent} from 'api/generated';
import {EventType} from 'types';
import {WebhookEventType} from '../api/generated';

export const getEventMessage = (event: ChargeEvent) => {
  const charge = event.object;
  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: charge.currency
  });

  const customer = charge.customer?.email || charge.customer?.phone || 'customer';
  const amount = currency.format((charge.amount || 0) / 100);
  const refundedAmount = currency.format((charge.lastRefundAmount || 0) / 100);
  const order = charge.orderId ? `for order #${charge.orderId}` : '';
  const statusMessage = charge.statusMessage;

  switch (event.type) {
    case EventType.CHARGE_AUTHORIZED:
      return `${customer} authorized payment for ${amount} ${order}`;
    case EventType.CHARGE_FAILED:
      return `Payment for ${amount} ${order} failed with error: ${statusMessage}`;
    case EventType.CHARGE_CANCELED:
      return `${customer} canceled payment for ${amount} ${order}`;
    case EventType.CHARGE_EXPIRED:
      return `Payment for ${amount} ${order} had expired`;
    case EventType.CHARGE_PENDING:
      return `Waiting for ${customer} to pay ${amount} ${order}`;
    case EventType.CHARGE_PENDING_PROCESSING:
      return `Waiting for ${customer} to complete the payment of ${amount} ${order}`;
    case EventType.CHARGE_REFUNDED:
      return `Payment for ${amount} ${order} was fully refunded`;
    case EventType.CHARGE_PARTIALLY_REFUNDED:
      return `${refundedAmount} was refunded ${order}`;
    case EventType.CHARGE_SUCCEEDED:
      return `${customer} paid ${amount} ${order}`;
    case EventType.CHARGE_PAID_OUT:
      return `${customer} received ${amount} ${order}`;
    case EventType.CHARGE_CAPTURED:
      return `Payment for ${amount} ${order} was captured`;
    case EventType.CHARGE_UPDATED:
      return `Payment for ${amount} ${order} was updated`;
  }

  return null;
};

export const getEventTooltipMessage = (eventType: EventType) => {
  switch (eventType) {
    case EventType.CHARGE_AUTHORIZED:
      return 'Payment has been authorized';
    case EventType.CHARGE_FAILED:
      return 'Payment has failed';
    case EventType.CHARGE_CANCELED:
      return 'Payment has been cancelled by customer';
    case EventType.CHARGE_EXPIRED:
      return 'Payment has expired';
    case EventType.CHARGE_PENDING:
      return 'Payment has been started, waiting for customer action';
    case EventType.CHARGE_PENDING_PROCESSING:
      return 'Customer has initiated payment process, waiting for confirmation';
    case EventType.CHARGE_REFUNDED:
      return 'A refund has been made by customer';
    case EventType.CHARGE_PARTIALLY_REFUNDED:
      return 'A partial refund has been made by customer';
    case EventType.CHARGE_SUCCEEDED:
      return 'Payment has been successfully processed';
    case EventType.CHARGE_PAID_OUT:
      return 'Payout has been successfully processed';
    case EventType.CHARGE_CAPTURED:
      return 'Payment has been captured by the merchant';
    case EventType.CHARGE_UPDATED:
      return 'Payment has been updated';
  }
};

export const getWebhookEventTypes = () => {
  // Convert the object to an array of its keys
  const keys = Object.keys(WebhookEventType);

  // Group keys by their prefixes
  const grouped = keys.reduce((acc: any, current) => {
    let prefix, restLabel;
    let rest; // Declare rest here

    if (current.startsWith('ACCOUNT_INVOICE')) {
      // Special case for ACCOUNT_INVOICE to treat it as a separate category
      prefix = 'ACCOUNT_INVOICE';
      restLabel = current.slice(prefix.length + 1).toLowerCase();
    } else {
      [prefix, ...rest] = current.split('_');
      restLabel = rest.join('_').toLowerCase();
    }

    if (!acc[prefix]) {
      acc[prefix] = [];
    }

    acc[prefix].push({
      label: `${prefix.toLowerCase()}.${restLabel}`,
      value: current
    });

    return acc;
  }, {});

  // Transform the grouped object into the desired array format
  return Object.entries(grouped).map(([key, options]) => ({
    label:
      key.charAt(0) +
      key
        .slice(1)
        .toLowerCase()
        .replace(/_invoice/, ' Invoice'),
    options
  }));
};
